import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Accordion, Button, Card, Link, Text, Image, CardColors } from '@/ui'
import { useReselector } from '@/utils/sharedHooks'
import {
  getCurrentUser,
  selectIsInUnservicedFreeTrial,
} from '@/selectors/user.selectors'
import { useAppDispatch } from '@/utils/typeHelpers'
import { postCompleteOnboarding } from '@/features/user/dashboard/user-action-items/lib/userActionItems.slice'
import { updateUser } from '@/actions/userActions'
import { useAnalyticsTrack, useAnalyticsView } from '@/features/amplitude'
import { postCreateNewTicket } from '@/v2-components/conversations/actions'
import { selectPlaidItems } from '@/selectors/financeSelectors'
import {
  StatementStatus,
  StatementStatusType,
  getAccountUploadStatuses,
} from '@/actions/financialAccountActions'
import { ONBOARDING_V2_ROUTE_NAMES } from './config'
import { logHeardError } from '@/utils/errorHelpers'
import { TypographyStyles } from '@/styles'

const MISSING_FI_TICKET_SUBJECT = 'Onboarding missing FI from Plaid'
const MISSING_FI_TICKET_COPY =
  'This user has indicated during onboarding that one or more of their business bank accounts cannot be connected via Plaid.'
const MISSING_FI_TICKET_TAGS = ['onboarding_missing_fi']

const ONBOARDING_LBA_HEADER = 'Onboarding Limited Bank Access Request'
const ONBOARDING_LBA_TICKET_COPY =
  'This user has indicated during onboarding they are interested in setting up limited bank access to their business bank account(s). Supported institutions:'
const ONBOARDING_LBA_TICKET_TAGS = ['onboarding_lba']

export const OtherFeature = ({
  imgSrc,
  title,
  description,
}: {
  imgSrc: string
  title: string
  description: string | React.ReactNode
}) => (
  <div className="flex flex-row items-center w-full gap-6 mb-4">
    <Image src={imgSrc} alt="feature_image" objectFit="scaleDown" width={100} />
    <div className="flex-grow w-full">
      <Text variant={TypographyStyles.H3} className="mb-2">
        {title}
      </Text>
      <Text>{description}</Text>
    </div>
  </div>
)

export const SetupComplete = () => {
  const user = useReselector(getCurrentUser)
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const track = useAnalyticsTrack()
  const pageView = useAnalyticsView()
  const ahaMoment = false
  const [statuses, setStatuses] = useState<StatementStatus[]>()
  const [loading, setLoading] = useState(false)
  const plaidItems = useReselector(selectPlaidItems)
  const isInUnservicedFreeTrial = useReselector(selectIsInUnservicedFreeTrial)

  useEffect(() => {
    pageView('register for group onboarding call')
  }, [pageView])

  useEffect(() => {
    const fetchStatuses = async () => {
      const statusesResponse = await dispatch(getAccountUploadStatuses())
      if (statusesResponse) {
        setStatuses(statusesResponse)
      }
    }
    fetchStatuses()
  }, [dispatch])

  useEffect(() => {
    if (
      user?.lastOnboardingScreen !==
      ONBOARDING_V2_ROUTE_NAMES.ONBOARDING_COMPLETE
    ) {
      const update = async () => {
        await updateUser(user?.id, {
          lastOnboardingScreen: ONBOARDING_V2_ROUTE_NAMES.ONBOARDING_COMPLETE,
        })(dispatch)
      }

      update()
    }
  }, [user, dispatch])

  const lbaSupportedInstitutions = useMemo(() => {
    if (!statuses) return []
    const plaidItemArray = Object.values(plaidItems)
    const filteredItems = plaidItemArray.map((item) => {
      const accounts = item.accounts.filter((account) =>
        statuses.some(
          (status) =>
            status.id === account.id &&
            status.status === StatementStatusType.LIMITED_BANK_ACCESS
        )
      )
      return {
        ...item,
        accounts,
      }
    })
    return filteredItems.filter((item) => item.accounts.length > 0)
  }, [plaidItems, statuses])

  const sendOnboardingCompleteSideEffects = async () => {
    // dont create tickets / send emails if onboarding is already completed
    if (user?.onboardingItemsCompletedAt) return
    // create internal ZD ticket if user indicated during onboarding that one or more of their business bank accounts cannot be connected via Plaid
    if (user?.financialProfile?.hasIncompatibleAccounts) {
      try {
        dispatch(
          postCreateNewTicket({
            comment: {
              body: MISSING_FI_TICKET_COPY,
              htmlBody: `<p>${MISSING_FI_TICKET_COPY}</p>`,
            },
            subject: MISSING_FI_TICKET_SUBJECT,
            tags: MISSING_FI_TICKET_TAGS,
          })
        )
      } catch (error) {
        logHeardError(error)
      }
    }
    // create internal ZD ticket if user indicated during onboarding they are interested in setting up limited bank access to their business bank account(s)
    if (user?.financialProfile?.limitedBankAccessInterest) {
      try {
        dispatch(
          postCreateNewTicket({
            comment: {
              body: `${ONBOARDING_LBA_TICKET_COPY} ${lbaSupportedInstitutions.map((item) => item.institutionName).toString()}`,
              htmlBody: `<p>${ONBOARDING_LBA_TICKET_COPY} 
          <ul>
            ${lbaSupportedInstitutions.map((item) => `<li>${item.institutionName}</li>`)}
          </ul>
        </p>`,
            },
            subject: ONBOARDING_LBA_HEADER,
            tags: ONBOARDING_LBA_TICKET_TAGS,
          })
        )
      } catch (error) {
        logHeardError(error)
      }
    }
    await dispatch(postCompleteOnboarding())
  }

  const completeSetup = async () => {
    setLoading(true)
    track('completed onboarding items')
    await sendOnboardingCompleteSideEffects()
    navigate('/dashboard')
    window.location.reload()
    setLoading(false)
  }

  const setupHeaderText = isInUnservicedFreeTrial
    ? 'Your Heard trial experience is ready'
    : 'Your Heard account is ready'

  const otherFeatureItems = () => {
    if (isInUnservicedFreeTrial) {
      return (
        <>
          <OtherFeature
            imgSrc="https://heard-images.s3.amazonaws.com/assets/chart-tablet.svg"
            title="Financial Insights"
            description="Detailed metrics on your income and expenses"
          />
          <OtherFeature
            imgSrc="https://heard-images.s3.amazonaws.com/assets/booklist.svg"
            title="Guides and Resources"
            description="Learn all the tips and tricks of managing your private practice finances."
          />
          <OtherFeature
            imgSrc="https://heard-images.s3.amazonaws.com/assets/green_piggy_bank.svg"
            title="Stay Compliant with Taxes"
            description="Preview the Tax experience with Heard, and learn how we help your practice stay compliant"
          />
        </>
      )
    } else {
      return (
        <>
          <OtherFeature
            imgSrc="https://heard-images.s3.amazonaws.com/assets/chart-tablet.svg"
            title="Financial Insights"
            description="Detailed metrics on your income and expenses"
          />
          <OtherFeature
            imgSrc="https://heard-images.s3.amazonaws.com/assets/green_piggy_bank.svg"
            title="Budgeting Guides"
            description="Set up how much to put aside for taxes and pay"
          />
        </>
      )
    }
  }
  return (
    <div className="flex flex-col space-y-6">
      <Text variant={TypographyStyles.H2}>{setupHeaderText}</Text>

      <Card color={CardColors.PRIORITY}>
        <Image
          src="https://heard-images.s3.amazonaws.com/assets/laptop_chair.svg"
          alt="office_chair"
          width={174}
          height={168}
          objectFit="scaleDown"
        />
      </Card>

      <Text variant={TypographyStyles.BODY_LG}>
        {isInUnservicedFreeTrial
          ? `Thanks, ${user?.firstName}. We're so excited to have you try the Heard experience.\n\nHere's what to look forward to:`
          : `Thanks, ${user?.firstName}. We have everything we need to start working on your books. Once they're complete, here's what to look forward to:`}
      </Text>

      <div className="flex flex-col items-center">{otherFeatureItems()}</div>

      {user?.financialProfile?.hasIncompatibleAccounts && (
        <div className="SetupComplete__card space-y-4">
          <div className="SetupComplete__card-header flex items-start">
            <div className="SetupComplete__copy space-y-2 flex-1">
              <Text
                variant={TypographyStyles.H3}
                className="SetupComplete__row-item"
              >
                Still have accounts that couldn&apos;t be connected?
              </Text>
              <Text>
                We&apos;ll follow up via Conversations regarding any account(s)
                that were incompatible with Plaid.
              </Text>
              <Text>
                In the meantime,{' '}
                <b>
                  we recommend replacing them with accounts at compatible banks.
                </b>{' '}
                This allows us to provide you the most consistent service.
              </Text>
            </div>
            <Image
              src="https://heard-images.s3.amazonaws.com/assets/pavilion_exclamation_mark.png"
              alt="warning icon"
              className="w-[78px] h-[65px] ml-8"
            />
          </div>

          <Accordion type="single" collapsible variant="text" className="mb-5">
            <Accordion.Item value="business-account">
              <Accordion.Trigger>
                What if I don&apos;t have a business account at all?
              </Accordion.Trigger>
              <Accordion.Content>
                <Text>
                  Here are some widely used banks that are compatible with Plaid
                  and used by Heard customers for their business bank accounts:
                  <ul className="SetupComplete-list list-disc pl-5 mt-2">
                    {[
                      {
                        name: 'Bluevine',
                        url: 'https://www.bluevine.com/partner/heard-checking',
                      },
                      {
                        name: 'Bank of America',
                        url: 'https://www.bankofamerica.com/smallbusiness/deposits/business-advantage-banking/',
                      },
                      {
                        name: 'BECU',
                        url: 'https://www.becu.org/business-banking/checking',
                      },
                      {
                        name: 'Chase',
                        url: 'https://www.chase.com/business/banking/checking',
                      },
                      {
                        name: 'TD Bank',
                        url: 'https://www.td.com/us/en/small-business/checking-accounts',
                      },
                      {
                        name: 'Wells Fargo',
                        url: 'https://www.wellsfargo.com/biz/checking/',
                      },
                    ].map(({ name, url }) => (
                      <li key={url}>
                        <Link href={url} newPage>
                          {name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </Text>
              </Accordion.Content>
            </Accordion.Item>
          </Accordion>

          <Accordion type="single" collapsible variant="text">
            <Accordion.Item value="account-replacement">
              <Accordion.Trigger>
                What if I don&apos;t want to replace my account(s)?
              </Accordion.Trigger>
              <Accordion.Content>
                <Text className="mb-1">
                  Our team will reach out to help you with your situation.
                  However, we recommend replacing your accounts, as
                  Plaid-incompatible accounts significantly delay bookkeeping
                  and access to your reports.
                </Text>
                <Text>
                  We might not be able to support the account(s) if it prevents
                  us from providing consistent service.
                </Text>
              </Accordion.Content>
            </Accordion.Item>
          </Accordion>
        </div>
      )}

      <div className="flex flex-col items-center space-y-3">
        {ahaMoment && (
          <Button variant="primary" onClick={completeSetup} loading={loading}>
            Get Set Up For Success
          </Button>
        )}
        <Button variant="secondary" onClick={completeSetup} loading={loading}>
          View Dashboard
        </Button>
      </div>
    </div>
  )
}
